import { useEffect, useState, useMemo, useCallback } from "react";
import { api, money, dateOnly, formatApiError } from "@/lib/api";
import { Plus, Wrench, X, Calendar, ListChecks, ClipboardList, CheckCircle2, Circle } from "lucide-react";import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";

const TABS = [
    { id: "list", label: "Lista", icon: ClipboardList },
    { id: "calendar", label: "Calendario", icon: Calendar },
];

const CATEGORIES = [
    ["mecanico", "Mecánico"],
    ["electrico", "Eléctrico"],
    ["biomedico", "Biomédico"],
    ["frenos", "Frenos"],
    ["llantas", "Llantas"],
    ["limpieza", "Limpieza"],
    ["sanitizacion", "Sanitización"],
];

export default function Maintenance() {
    const [tab, setTab] = useState("list");
    const [list, setList] = useState([]);
    const [calendar, setCalendar] = useState([]);
    const [units, setUnits] = useState([]);
    const [templates, setTemplates] = useState({});
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // {id, ...} para abrir checklist
    const [form, setForm] = useState({
        unit_id: "", type: "preventivo", category: "mecanico", description: "",
        workshop: "", cost: 0,
        scheduled_date: new Date().toISOString().slice(0, 10),
        start_date: new Date().toISOString().slice(0, 10),
        status: "programado",
        checklist: [],
        responsible: "",
        notes: "",
    });

    const load = useCallback(async () => {
        try {
            const [m, u, t, cal] = await Promise.all([
                api.get("/maintenance"),
                api.get("/units"),
                api.get("/maintenance/checklist-templates"),
                api.get("/maintenance/calendar?days=90"),
            ]);
            if (m?.data) setList(m.data);
            if (u?.data) setUnits(u.data);
            if (t?.data) setTemplates(t.data);
            if (cal?.data) setCalendar(cal.data);
        } catch (e) { console.warn(e); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onCategoryChange = (category) => {
        setForm((f) => ({ ...f, category, checklist: (templates[category] || []).map((x) => ({ ...x })) }));
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/maintenance", form);
            toast.success("Mantenimiento registrado");
            setOpen(false);
            setForm({
                unit_id: "", type: "preventivo", category: "mecanico", description: "",
                workshop: "", cost: 0,
                scheduled_date: new Date().toISOString().slice(0, 10),
                start_date: new Date().toISOString().slice(0, 10),
                status: "programado",
                checklist: [],
                responsible: "",
                notes: "",
            });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const closeMaintenance = async (id) => {
        try { await api.patch(`/maintenance/${id}/close`); toast.success("Mantenimiento cerrado"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const startMaintenance = async (id) => {
        try { await api.patch(`/maintenance/${id}`, { status: "en_progreso" }); toast.success("Iniciado"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const toggleChecklistItem = async (mid, idx) => {
        const m = list.find((x) => x.id === mid) || editing;
        if (!m) return;
        const nextChecklist = (m.checklist || []).map((x, i) => i === idx ? { ...x, done: !x.done } : x);
        try {
            const r = await api.patch(`/maintenance/${mid}`, { checklist: nextChecklist });
            setList((l) => l.map((x) => x.id === mid ? r.data : x));
            if (editing?.id === mid) setEditing(r.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    // Agrupa el calendario por mes
    const calendarByMonth = useMemo(() => {
        const groups = {};
        for (const ev of calendar) {
            const d = (ev.calendar_date || "").slice(0, 7);
            if (!d) continue;
            (groups[d] = groups[d] || []).push(ev);
        }
        return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    }, [calendar]);

    return (
        <div className="space-y-4" data-testid="maintenance-page">
            <div className="flex justify-between items-center flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                        <Wrench className="w-6 h-6 text-red-600" /> Mantenimiento
                        <HelpTip text="Registra y agenda mantenimientos preventivos y correctivos de la flotilla. Cada mantenimiento puede tener un checklist verificable por el mecánico." />
                    </h2>
                    <div className="text-xs text-slate-500 font-mono">
                        {list.length} registros · {list.filter(m => m.status === "en_progreso").length} en progreso · {list.filter(m => m.status === "programado").length} programados
                    </div>
                </div>
                <button onClick={() => setOpen(true)} className="fm-btn-accent" data-testid="new-maintenance-btn">
                    <Plus className="w-3.5 h-3.5" /> Nuevo registro
                </button>
            </div>

            <div className="border-b border-slate-200 flex flex-wrap gap-1">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border-b-2 transition flex items-center gap-2 ${active ? "border-red-600 text-red-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
                            data-testid={`maint-tab-${t.id}`}
                        >
                            <Icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "list" && (
                <div className="fm-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 border-b border-slate-200">
                            {["Folio", "Unidad", "Tipo", "Categoría", "Descripción", "Programada", "Estatus", "Checklist", "Costo", ""].map((h) => (
                                <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                            ))}
                        </tr></thead>
                        <tbody>
                            {list.map((m) => {
                                const checklistTotal = (m.checklist || []).length;
                                const checklistDone = (m.checklist || []).filter((x) => x.done).length;
                                return (
                                    <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="py-2 px-3 font-mono text-xs">{m.folio}</td>
                                        <td className="py-2 px-3 font-mono">{m.unit_number}</td>
                                        <td className="py-2 px-3 capitalize">{m.type}</td>
                                        <td className="py-2 px-3 capitalize">{m.category}</td>
                                        <td className="py-2 px-3 max-w-xs truncate">{m.description}</td>
                                        <td className="py-2 px-3 text-xs font-mono">{dateOnly(m.scheduled_date || m.start_date)}</td>
                                        <td className="py-2 px-3">
                                            <span className={`fm-badge ${m.status === "completado" ? "fm-badge-positive" : m.status === "en_progreso" ? "fm-badge-warning" : "fm-badge-neutral"}`}>{m.status}</span>
                                        </td>
                                        <td className="py-2 px-3">
                                            {checklistTotal > 0 ? (
                                                <button onClick={() => setEditing(m)} className="text-xs font-mono hover:underline" data-testid={`open-checklist-${m.id}`}>
                                                    {checklistDone}/{checklistTotal} <ListChecks className="w-3 h-3 inline" />
                                                </button>
                                            ) : <span className="text-[10px] text-slate-400">—</span>}
                                        </td>
                                        <td className="py-2 px-3 text-right font-mono">{money(m.cost)}</td>
                                        <td className="py-2 px-3 text-xs space-x-2">
                                            <button onClick={() => setEditing(m)} className="text-slate-700 hover:text-red-600 underline" data-testid={`view-detail-${m.id}`}>Detalle</button>
                                            {m.status === "programado" && (
                                                <button onClick={() => startMaintenance(m.id)} className="text-amber-700 hover:underline" data-testid={`start-${m.id}`}>Iniciar</button>
                                            )}
                                            {m.status !== "completado" && (
                                                <button onClick={() => closeMaintenance(m.id)} className="text-slate-700 hover:text-red-600 underline" data-testid={`close-${m.id}`}>Cerrar</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {list.length === 0 && (
                        <div className="p-10 text-center text-sm text-slate-500"><Wrench className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />Sin mantenimientos.</div>
                    )}
                </div>
            )}

            {tab === "calendar" && (
                <div className="space-y-4">
                    {calendarByMonth.length === 0 && (
                        <div className="fm-card p-10 text-center text-sm text-slate-500">
                            <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                            Sin eventos programados ni en progreso en los próximos 90 días.
                        </div>
                    )}
                    {calendarByMonth.map(([month, events]) => (
                        <div key={month} className="fm-card overflow-hidden">
                            <div className="bg-slate-900 text-white px-4 py-2 text-xs uppercase tracking-widest font-bold">{month}</div>
                            <table className="w-full text-sm">
                                <thead><tr className="bg-slate-50 border-b border-slate-200">
                                    {["Fecha", "Unidad", "Categoría", "Descripción", "Estatus", "Checklist"].map((h) => (
                                        <th key={h} className="text-left py-2 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr></thead>
                                <tbody>
                                    {events.map((ev) => {
                                        const cd = (ev.checklist || []).length;
                                        const cdone = (ev.checklist || []).filter((x) => x.done).length;
                                        return (
                                            <tr key={ev.id} className="border-b border-slate-100">
                                                <td className="py-2 px-3 font-mono text-xs">{dateOnly(ev.calendar_date)}</td>
                                                <td className="py-2 px-3 font-mono">{ev.unit_number} {ev.plates && <span className="text-[10px] text-slate-500">· {ev.plates}</span>}</td>
                                                <td className="py-2 px-3 capitalize">{ev.category}</td>
                                                <td className="py-2 px-3">{ev.description}</td>
                                                <td className="py-2 px-3"><span className={`fm-badge ${ev.status === "completado" ? "fm-badge-positive" : ev.status === "en_progreso" ? "fm-badge-warning" : "fm-badge-neutral"}`}>{ev.status}</span></td>
                                                <td className="py-2 px-3 text-xs font-mono">{cd > 0 ? `${cdone}/${cd}` : "—"}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal nuevo mantenimiento */}
            {open && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="font-display font-bold text-lg">Registrar mantenimiento</h2>
                            <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={submit} className="p-5 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="fm-label">Unidad *</label>
                                    <select required value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })} className="fm-input" data-testid="m-unit">
                                        <option value="">Seleccionar</option>
                                        {units.map((u) => <option key={u.id} value={u.id}>{u.economic_number} · {u.plates}</option>)}
                                    </select>
                                </div>
                                <div><label className="fm-label">Tipo</label>
                                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="fm-input">
                                        <option value="preventivo">Preventivo</option>
                                        <option value="correctivo">Correctivo</option>
                                    </select>
                                </div>
                                <div><label className="fm-label">Categoría <HelpTip text="Al cambiar la categoría se precarga el checklist sugerido (puedes editarlo)." /></label>
                                    <select value={form.category} onChange={(e) => onCategoryChange(e.target.value)} className="fm-input" data-testid="m-category">
                                        {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                </div>
                                <div><label className="fm-label">Estatus</label>
                                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="fm-input">
                                        <option value="programado">Programado</option>
                                        <option value="en_progreso">En progreso</option>
                                        <option value="completado">Completado</option>
                                    </select>
                                </div>
                                <div><label className="fm-label">Fecha programada</label><input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Fecha de inicio</label><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="fm-input" /></div>
                                <div className="col-span-2"><label className="fm-label">Descripción *</label><input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="fm-input" data-testid="m-description" /></div>
                                <div><label className="fm-label">Taller</label><input value={form.workshop} onChange={(e) => setForm({ ...form, workshop: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Costo</label><input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value) || 0 })} className="fm-input" /></div>
                                <div className="col-span-2"><label className="fm-label">Responsable</label><input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} className="fm-input" placeholder="Nombre del mecánico o responsable" /></div>
                            </div>

                            {/* Checklist */}
                            {form.checklist.length > 0 && (
                                <div className="border-t border-slate-200 pt-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="font-bold text-sm flex items-center gap-2"><ListChecks className="w-4 h-4 text-red-600" /> Checklist ({form.category})</h4>
                                        <button type="button" onClick={() => setForm({ ...form, checklist: [...form.checklist, { label: "", done: false, note: "" }] })} className="text-xs text-red-600 hover:text-red-800 underline">+ Agregar punto</button>
                                    </div>
                                    <div className="space-y-2">
                                        {form.checklist.map((it, i) => (
                                            <div key={i} className="flex items-center gap-2">
                                                <input type="checkbox" checked={!!it.done} onChange={(e) => {
                                                    const c = [...form.checklist]; c[i].done = e.target.checked; setForm({ ...form, checklist: c });
                                                }} />
                                                <input value={it.label} onChange={(e) => {
                                                    const c = [...form.checklist]; c[i].label = e.target.value; setForm({ ...form, checklist: c });
                                                }} className="fm-input flex-1 text-xs" placeholder="Punto del checklist" />
                                                <button type="button" onClick={() => setForm({ ...form, checklist: form.checklist.filter((_, idx) => idx !== i) })} className="text-red-500 text-xs">×</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button type="button" onClick={() => setOpen(false)} className="fm-btn-outline">Cancelar</button>
                                <button type="submit" className="fm-btn-accent" data-testid="m-save">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal detalle completo + checklist */}
            {editing && (
                <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-xl w-full max-h-[88vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200">
                            <div>
                                <h3 className="font-display font-bold flex items-center gap-2"><ClipboardList className="w-4 h-4 text-red-600" /> Detalle de mantenimiento</h3>
                                <div className="text-[10px] font-mono text-slate-500 mt-0.5">{editing.folio}</div>
                            </div>
                            <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Unidad</div><div className="font-mono">{editing.unit_number}</div></div>
                                <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tipo</div><div className="capitalize">{editing.type}</div></div>
                                <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Categoría</div><div className="capitalize">{editing.category}</div></div>
                                <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Estatus</div><div><span className={`fm-badge ${editing.status === "completado" ? "fm-badge-positive" : editing.status === "en_progreso" ? "fm-badge-warning" : "fm-badge-neutral"}`}>{editing.status}</span></div></div>
                                <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Programada</div><div className="font-mono">{dateOnly(editing.scheduled_date)}</div></div>
                                <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Inicio</div><div className="font-mono">{dateOnly(editing.start_date)}</div></div>
                                <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Cierre</div><div className="font-mono">{editing.end_date ? dateOnly(editing.end_date) : "—"}</div></div>
                                <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Costo</div><div className="font-mono font-bold">{money(editing.cost)}</div></div>
                                <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Taller / Proveedor</div><div>{editing.workshop || "—"}</div></div>
                                <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Responsable</div><div>{editing.responsible || "—"}</div></div>
                                <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Km al servicio</div><div className="font-mono">{editing.km_at_service?.toLocaleString() || "—"}</div></div>
                                <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Descripción</div><div className="text-sm bg-slate-50 p-2 rounded-sm">{editing.description}</div></div>
                                {editing.notes && (
                                    <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Notas</div><div className="text-sm bg-slate-50 p-2 rounded-sm whitespace-pre-wrap">{editing.notes}</div></div>
                                )}
                            </div>

                            {(editing.checklist || []).length > 0 && (
                                <div className="border-t border-slate-200 pt-3">
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Checklist</div>
                                    <div className="space-y-1">
                                        {(editing.checklist || []).map((it, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => toggleChecklistItem(editing.id, i)}
                                                className="w-full flex items-start gap-2 py-1.5 px-2 rounded-sm hover:bg-slate-50 text-left"
                                                data-testid={`checklist-${editing.id}-${i}`}
                                            >
                                                {it.done ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> : <Circle className="w-4 h-4 text-slate-400 mt-0.5" />}
                                                <span className={`text-sm flex-1 ${it.done ? "line-through text-slate-400" : ""}`}>{it.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-200">
                                {editing.status === "programado" && (
                                    <button onClick={() => { startMaintenance(editing.id); setEditing(null); }} className="fm-btn-primary text-xs">Iniciar</button>
                                )}
                                {editing.status !== "completado" && (
                                    <button onClick={() => { closeMaintenance(editing.id); setEditing(null); }} className="fm-btn-accent text-xs">Marcar como completado</button>
                                )}
                                <button onClick={() => setEditing(null)} className="fm-btn-outline text-xs ml-auto">Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
